import { useNotification } from "@context/NotificationContext";
import type {
  VesselNominationDetailsResponse,
  VesselNominationRequestPayload,
} from "@services/vessel-nomination/types";
import {
  deleteNomination,
  postNomination,
  putNomination,
  QUERY_KEY,
  updateNomination,
} from "@services/vesselNominationApi";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import mapVesselNominationFormToPayload from "@utils/mappers/vesselNominationMapper";
import { paths } from "@utils/navigation";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { QUERY_KEY as NOMINATION_QUERY_KEY } from "../../nomination-details/VesselNominationDetails";
import type {
  getVesselNominationFormSchema,
  VesselNominationFormData,
  VesselNominationValidatedFormData,
} from "../schema/vesselNominationFormSchema";

const LANG_BASE = "vessel-nomination.notifications";

interface UseVesselNominationProps {
  formValues?: VesselNominationFormData;
  schema?: ReturnType<typeof getVesselNominationFormSchema>;
  resetForm?: () => void;
  nominationId?: string;
}

interface UseVesselNominationResult {
  onSubmit: () => Promise<void>;
  onSave: () => Promise<void>;
  isSubmitPending: boolean;
  isSavePending: boolean;
  onDiscard: () => Promise<void>;
  nominationId?: string;
}

export const useVesselNomination = ({
  formValues,
  schema,
  resetForm,
  nominationId,
}: UseVesselNominationProps): UseVesselNominationResult => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const qc = useQueryClient();
  const { notify } = useNotification();
  const { t } = useTranslation();

  const getVesselNominationDetails = () =>
    (
      (state as {
        vesselNominationDetails?: VesselNominationDetailsResponse;
      } | null) ?? { vesselNominationDetails: undefined }
    ).vesselNominationDetails;

  const addNomination = useMutation<
    unknown,
    unknown,
    { payload: VesselNominationRequestPayload; id?: string; isDraft?: boolean }
  >({
    mutationFn: ({ payload, id }) => {
      // update existing vessel nomination from 1st step to second step
      if (id) {
        return updateNomination({
          id,
          nominationPayload: {
            ...payload,
            status: payload.status === "DRAFT" ? null : payload.status,
          },
        });
        // create new vessel nomination
      } else {
        return postNomination({ nominationPayload: payload });
      }
    },
    onSuccess: () => {
      notify({
        title: t(`${LANG_BASE}.vessel-nomination-submitted-success-title`),
        type: "success",
      });

      resetForm?.();
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
      qc.invalidateQueries({ queryKey: [NOMINATION_QUERY_KEY] });
      navigate(`/${paths.vesselNomination.index}`);
    },
    onError: (error: any) => {
      notify({
        title: t(`${LANG_BASE}.vessel-nomination-submitted-error-title`),
        description: error?.response?.data?.detail ?? error.message,
        type: "error",
      });
    },
  });

  const saveDraft = useMutation<
    unknown,
    unknown,
    { payload: VesselNominationRequestPayload; id?: string }
  >({
    mutationFn: ({ payload, id }) => {
      return id
        ? putNomination({
            nominationId: id,
            nominationPayload: { ...payload, status: "DRAFT" },
          })
        : postNomination({ nominationPayload: { ...payload, status: "DRAFT" } });
    },
    onSuccess: () => {
      notify({
        title: t(`${LANG_BASE}.vessel-nomination-draft-saved-success-title`),
      });
      resetForm?.();
      qc.invalidateQueries({ queryKey: ["registeredVessels"] });
      navigate(`/${paths.vesselNomination.index}`);
    },
    onError: (error: any) => {
      notify({
        title: t(`${LANG_BASE}.vessel-nomination-draft-saved-error-title`),
        description: error?.response?.data?.detail ?? error.message,
        type: "error",
      });
    },
  });

  const discardDraft = useMutation({
    mutationFn: ({ id }: { id: string }) => deleteNomination({ id }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
      navigate(`/${paths.vesselNomination.index}`);
    },
    onError: (error: any) => {
      notify({
        title: t(`${LANG_BASE}.vessel-draft-discard-error-title`),
        description: error?.response?.data?.detail ?? error.message ?? t("common.try-again-later"),
        type: "error",
      });
    },
  });

  const onSubmit = async (): Promise<void> => {
    if (!formValues || !schema) return;

    const vesselNominationDetails = getVesselNominationDetails();
    const shouldUpdateExistingNomination = !!vesselNominationDetails?.id;

    const validatedFormValues: VesselNominationValidatedFormData = schema.parse(formValues);
    const vesselNominationPayload: VesselNominationRequestPayload =
      mapVesselNominationFormToPayload(validatedFormValues, vesselNominationDetails);

    if (shouldUpdateExistingNomination) {
      addNomination.mutate({ payload: vesselNominationPayload, id: vesselNominationDetails?.id });
    } else {
      addNomination.mutate({ payload: vesselNominationPayload });
    }
  };

  const onSave = async (): Promise<void> => {
    if (!formValues || !schema) return;
    const vesselNominationDetails = getVesselNominationDetails();

    const nominationPayload: VesselNominationRequestPayload = mapVesselNominationFormToPayload(
      formValues,
      vesselNominationDetails,
    );

    if (nominationId) {
      saveDraft.mutate({
        payload: nominationPayload,
        id: nominationId,
      });
    } else {
      saveDraft.mutate({ payload: nominationPayload });
    }
  };

  const onDiscard = async (): Promise<void> => {
    await discardDraft.mutateAsync({ id: nominationId! });
  };

  return {
    onSubmit,
    onSave,
    onDiscard,
    isSubmitPending: addNomination.isPending,
    isSavePending: saveDraft.isPending,
  };
};
